import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, Star, MessageSquare, Users, TrendingUp, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SurveyResults = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchResults();
  }, [courseId]);

  const fetchResults = async () => {
    try {
      const res = await axios.get(`${API_URL}/courses/${courseId}/survey-results`);
      setResults(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar los resultados');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/courses/${courseId}/survey-results/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `encuesta_${courseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (err) {
      toast.error('Error al descargar el PDF');
    } finally {
      setDownloading(false);
    }
  };

  const getColorForRating = (rating) => {
    if (rating >= 4.5) return 'text-green-600 bg-green-50';
    if (rating >= 3.5) return 'text-blue-600 bg-blue-50';
    if (rating >= 2.5) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const StarRating = ({ rating }) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= Math.round(rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-200'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  if (!results) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">No se pudieron cargar los resultados</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/courses/${courseId}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al curso
          </Button>
          <Button 
            variant="outline" 
            onClick={handleDownloadPdf} 
            disabled={downloading}
            className="border-[#004E89] text-[#004E89] hover:bg-[#004E89] hover:text-white ml-auto"
            data-testid="survey-download-pdf-button"
          >
            {downloading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                Generando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Descargar PDF
              </>
            )}
          </Button>
        </div>

        {/* Title */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Resultados de Encuesta de Satisfacción
          </h1>
          <p className="text-slate-600">{results.course_name}</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Respuestas recibidas</p>
                <p className="text-2xl font-bold text-slate-900">{results.total_responses}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${getColorForRating(results.overall_average)}`}>
                <TrendingUp className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Media general</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold text-slate-900">{results.overall_average}</p>
                  <span className="text-slate-400">/5</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <MessageSquare className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Comentarios</p>
                <p className="text-2xl font-bold text-slate-900">{results.comments?.length || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {results.total_responses === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Sin respuestas aún</h3>
            <p className="text-slate-500">
              Todavía no se han recibido respuestas a la encuesta de satisfacción.
            </p>
          </div>
        ) : (
          <>
            {/* Detailed Results */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="font-semibold text-lg text-slate-900">Resultados por ítem</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {results.items?.map((item) => {
                  const itemData = results.averages[item.id];
                  if (!itemData) return null;
                  
                  return (
                    <div key={item.id} className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-medium text-slate-900">{item.label}</h3>
                          <p className="text-sm text-slate-500">{item.description}</p>
                        </div>
                        <div className="text-right">
                          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${getColorForRating(itemData.average)}`}>
                            <span className="font-bold">{itemData.average}</span>
                            <span className="text-sm opacity-70">/5</span>
                          </div>
                          <div className="mt-1">
                            <StarRating rating={itemData.average} />
                          </div>
                        </div>
                      </div>
                      
                      {/* Distribution */}
                      <div className="mt-4 space-y-2">
                        {[5, 4, 3, 2, 1].map((rating) => {
                          const count = itemData.distribution[rating] || 0;
                          const percentage = itemData.count > 0 ? (count / itemData.count) * 100 : 0;
                          
                          return (
                            <div key={rating} className="flex items-center gap-3 text-sm">
                              <span className="w-6 text-right text-slate-500">{rating}</span>
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                              <div className="flex-1">
                                <Progress value={percentage} className="h-2" />
                              </div>
                              <span className="w-12 text-right text-slate-500">
                                {count} ({Math.round(percentage)}%)
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Comments */}
            {results.comments?.length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h2 className="font-semibold text-lg text-slate-900">
                    Comentarios de los alumnos ({results.comments.length})
                  </h2>
                </div>
                <div className="divide-y divide-slate-100">
                  {results.comments.map((comment, index) => (
                    <div key={index} className="p-6">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                          <span className="text-sm text-slate-500">#{index + 1}</span>
                        </div>
                        <p className="text-slate-700 italic">"{comment}"</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default SurveyResults;
